/*
 * CSS color string helpers for the element style panel. getComputedStyle
 * returns colors as legacy comma rgb()/rgba(); the parser also accepts CSS
 * Color 4 space/slash syntax (`rgb(15 23 41 / 0.5)`) for user-typed input;
 * users type hex; react-colorful wants rgba() strings — these convert between
 * them. Anything unparseable (keywords like `red`/`inherit`, hsl(), gradients)
 * returns null and the caller falls back to showing the raw string.
 */

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const BARE_HEX_RE = /^([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_COMMA_RE = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*([\d.]+)\s*)?\)$/i;
const RGB_SPACE_RE = /^rgba?\(\s*(\d{1,3})\s+(\d{1,3})\s+(\d{1,3})\s*(?:\/\s*([\d.]+%?)\s*)?\)$/i;

/** r/g/b are integers 0–255; a is 0–1. Always fully resolved (no NaN). */
export interface RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

export function parseCssColor(css: string): RgbaColor | null {
  const s = css.trim().toLowerCase();

  if (s === 'transparent') {
    return { r: 0, g: 0, b: 0, a: 0 };
  }

  const hex = s.match(HEX_RE);

  if (hex) {
    let h = hex[1];

    if (h.length === 3) {
      h = h
        .split('')
        .map((c) => c + c)
        .join('');
    }

    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
      a: h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
    };
  }

  const m = s.match(RGB_COMMA_RE) ?? s.match(RGB_SPACE_RE);

  if (!m) {
    return null;
  }

  const clampByte = (n: number) => Math.min(255, Math.max(0, n));
  let a = 1;

  if (m[4] !== undefined) {
    a = m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
    a = Math.min(1, Math.max(0, a));
  }

  return { r: clampByte(+m[1]), g: clampByte(+m[2]), b: clampByte(+m[3]), a };
}

export function cssColorToHex(css: string): string | null {
  const c = parseCssColor(css);

  if (!c) {
    return null;
  }

  const byte = (n: number) => n.toString(16).padStart(2, '0');
  const base = `#${byte(c.r)}${byte(c.g)}${byte(c.b)}`;

  return c.a < 1 ? `${base}${byte(Math.round(c.a * 255))}` : base;
}

export function toRgbaString(css: string): string | null {
  const c = parseCssColor(css);

  return c ? `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})` : null;
}

export function isTransparent(css: string): boolean {
  return parseCssColor(css)?.a === 0;
}

/**
 * Normalize a value typed into the color field's hex input into a CSS color
 * to apply. A bare hex body (`d9f26a`, `fff`, `0f172980`) gets a `#`; anything
 * else — keywords (`red`), `rgb()`/`hsl()` functions, an already-`#`-prefixed
 * hex — passes through as typed so the browser applies it, matching the old
 * raw <input> behavior (invalid CSS just no-ops in the browser). Empty input
 * returns null (nothing to apply). CSS color syntax is case-insensitive, so
 * lowercasing is safe and keeps hex output canonical.
 */
export function normalizeColorInput(raw: string): string | null {
  const trimmed = raw.trim();

  if (!trimmed) {
    return null;
  }

  return (BARE_HEX_RE.test(trimmed) ? `#${trimmed}` : trimmed).toLowerCase();
}
